import { Suspense, useEffect, useState } from "react";
import { MODELS, useMultiModel } from "../api";
import ErrorBoundary from "./ErrorBoundary";

// fit = max|min
function processSanityImage(url, { height, width, fit, dpr }) {
  const params = [];
  if (height) params.push(`h=${height}`);
  if (width) params.push(`w=${width}`);
  if (fit) params.push(`fit=${fit}`);
  if (dpr) params.push(`dpr=${dpr}`);
  else {
    if (height || width) params.push("fit=max");
  }

  return `${url}?${params.join("&")}`;
}

function toUrl(asset) {
  const [, id, dimensionString, format] = asset._ref.split("-");
  const filename = `${id}-${dimensionString}.${format}`;

  return `https://cdn.sanity.io/images/${
    import.meta.env.VITE_SANITY_PROJECT_ID
  }/production/${filename}`;
}

export function SanityImg({
  url: rawUrl,
  asset,
  width,
  height,
  fit,
  alt,
  decorative = false,
  ...rest
}) {
  const url = rawUrl || toUrl(asset);
  const x1 = processSanityImage(url, { height, width, fit, dpr: 1 });
  const x2 = processSanityImage(url, { height, width, fit, dpr: 2 });
  const srcSet = [`${x1} 1x`, `${x2} 2x`].join(", ");

  return (
    <img
      src={x1}
      srcSet={srcSet}
      alt={alt}
      // `width`/`height` reserve the box (aspect ratio) so the browser can tell
      // which images are below the fold and actually honour `loading="lazy"`
      // instead of eagerly fetching everything.
      width={width}
      height={height}
      loading="lazy"
      decoding="async"
      {...rest}
      aria-hidden={decorative}
    />
  );
}

const imgFallback = <div style={{ height: "1.5rem" }} />;

/** @param {Record<string, any>} props */
const ImgForPathInner = ({
  width,
  height,
  contain: _contain,
  path,
  alt,
  className,
  ...rest
}) => {
  // `quiet`: a missing file is an EXPECTED absence for an inline image, so the
  // lookup failure degrades to `undefined` + a single dev warning instead of
  // throwing into an error boundary (which makes React log a full stack trace).
  const [modelData] = useMultiModel([path ? MODELS.S3_FILE_URL(path) : null], {
    quiet: true,
  });
  const [error, setError] = useState(null);
  const [src, setSrc] = useState();

  useEffect(() => {
    const fileUrl = modelData?.fileUrl;
    if (!fileUrl) return;

    setSrc(fileUrl);
    setError(null);

    // Only revoke if this effect created an object URL.
    // If fileUrl is a remote URL, do not revoke.
    return () => {
      if (fileUrl.startsWith("blob:")) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [modelData?.fileUrl]);

  if (error) {
    console.warn("Error displaying the image at ", path, error);
    return imgFallback;
  }

  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onError={() => {
          setSrc(null);
          setError("Image not available");
        }}
        className={className}
        {...rest}
        width={width}
        height={height}
      />
    );
  }

  // No src yet (loading) or the lookup quietly failed → show the placeholder.
  return imgFallback;
};

/**
 * Loads an optional S3 image. A missing file (the `/upload` lookup 404s) is an
 * EXPECTED absence for an inline image, so it is isolated in its own Suspense +
 * ErrorBoundary and degrades to a small placeholder instead of throwing up to
 * `RouteBoundary` (which would blank the route and, since the chrome renders
 * avatars too, re-throw into its own fallback in an endless refetch loop).
 *
 * In DEV the inner read uses `useMultiModel`'s `quiet` mode, so a failure becomes
 * a single warning + placeholder (no thrown error → no noisy React stack trace)
 * and these wrappers stay inert. In PRODUCTION `quiet` is ignored: the read
 * suspends and throws, and these wrappers are what catch it.
 *
 * Keyed by `path` so switching images resets the boundary and local state.
 *
 * @param {Record<string, any>} props
 */
export const ImgForPath = (props) => (
  <ErrorBoundary key={props.path} fallback={imgFallback}>
    <Suspense fallback={imgFallback}>
      <ImgForPathInner {...props} />
    </Suspense>
  </ErrorBoundary>
);

/**
 * Converts an image file to WebP format with optional quality and size constraints.
 *
 * @param {File} file - The image file to be converted to WebP format
 * @param {number} [quality=0.8] - The quality of the WebP output, ranging from 0 to 1 (default: 0.8)
 * @param {number} [maxSize=3840] - The maximum width or height in pixels; the image will be scaled down proportionally if it exceeds this size (default: 3840)
 * @returns {Promise<File>} A promise that resolves to a new File object in WebP format with the original filename but .webp extension
 * @throws {Error} Throws an error if the canvas is not supported or if WebP conversion fails
 */
export async function fileToWebP(file, quality = 0.8, maxSize = 3840) {
  const bitmap = await createImageBitmap(file);

  const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
  const targetW = Math.round(bitmap.width * scale);
  const targetH = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  ctx.drawImage(bitmap, 0, 0, targetW, targetH);

  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, "image/webp", quality);
  });

  if (!blob) throw new Error("WebP conversion failed");

  return new File([blob], file.name.replace(/\.[^.]+$/, ".webp"), {
    type: "image/webp",
    lastModified: Date.now(),
  });
}
